import { DATE_FORMAT_SHORT } from '@findy-code/utils/date';
import { CompanyAiUsageTag } from '@findy-code/yuffie/shared/company-ai-usage-tag';
import { DateTime } from '@findy-code/yuffie/shared/date-time';
import { ReactComponent as TeamPlusAwardStar } from '@findy-code/yuffie/shared/images/company-ai-usage-star.svg';
import { NewItemTag } from '@findy-code/yuffie/shared/tag';
import { ButtonV2 } from '@findy-code/yuffie/ui/button/v2';
import { Heading } from '@findy-code/yuffie/ui/heading';
import { Stack } from '@findy-code/yuffie/ui/stack';
import { Text } from '@findy-code/yuffie/ui/text';
import { Tooltip } from '@findy-code/yuffie/ui/tooltip';
import { isAfter, subDays } from 'date-fns';
import Link from 'next/link';
import { useMemo } from 'react';

import type {
  JobDescriptionCompanyAiUsageFragment,
  TeamPlusAwardFragment,
} from '../../graphql';
import styles from './job-description-header.component.module.scss';

//@todo queryを作成後にFragment利用に変更する
type Props = {
  companyAiUsage?: JobDescriptionCompanyAiUsageFragment | null;
  title: string;
  companyId: number;
  companyName: string;
  updatedAt: string;
  publishedAt?: string | null;
  teamPlusAward?: TeamPlusAwardFragment | null;
};

/**
 * @todo ...募集要項・年収・条件の部分がutil定義されているのでリファクタして利用
 */
export const JobDescriptionHeader = ({
  companyAiUsage,
  title,
  companyId,
  companyName,
  updatedAt,
  publishedAt,
  teamPlusAward,
}: Props) => {
  const isNew = useMemo(() => {
    if (publishedAt) {
      const publishedAtDate = new Date(publishedAt);
      const oneWeekAgo = subDays(new Date(), 7);
      return isAfter(publishedAtDate, oneWeekAgo);
    }
    return false;
  }, [publishedAt]);

  return (
    <Stack aria-labelledby="job-description-title" as="section" spacing="l">
      <Stack spacing="xxs">
        <div className={styles.companyNameWrapper}>
          <Text font="heading-m">{companyName}</Text>
          <div className={styles.tagWrapper}>
            {companyAiUsage?.isUsingAi && <CompanyAiUsageTag />}
            {teamPlusAward && (
              <div>
                <div
                  className={styles.teamPlusAwardTag}
                  data-tooltip-id="tooltip-team-plus-award-tag"
                >
                  <TeamPlusAwardStar
                    aria-hidden="true"
                    height={16}
                    width={13}
                  />
                  <span>開発生産性の高い企業</span>
                </div>
                <Tooltip
                  closeEvents={{ mouseout: true }}
                  id="tooltip-team-plus-award-tag"
                  place="bottom-start"
                  style={{ zIndex: 1, maxWidth: 300 }}
                >
                  <div className={styles.teamPlusAwardTooltipContent}>
                    開発生産性の高い企業とは？：Findy
                    Team+を利用し開発生産性を可視化した企業の中で
                    <br />
                    「エンジニア組織の生産性指標」について組織規模別に特に優れた企業、
                    <br />
                    および過去半年間で生産性が大きく向上した企業として表彰された企業の事を指します。
                  </div>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
        <Heading
          font={{ base: 'heading-l', md: 'heading3' }}
          id="job-description-title"
        >
          {title}
        </Heading>
        <Stack
          align="center"
          direction="row"
          justify="space-between"
          spacing={0}
        >
          <p className={styles.updatedAtText}>
            <Text as="span" font="body-s">
              最終更新日:
              <DateTime dateTime={updatedAt} format={DATE_FORMAT_SHORT} />
            </Text>
            {isNew && <NewItemTag>New</NewItemTag>}
          </p>
          <Link legacyBehavior passHref href={`/companies/${companyId}`}>
            <ButtonV2 as="a" size="s" variant="tertiary">
              企業情報をみる
            </ButtonV2>
          </Link>
        </Stack>
      </Stack>
    </Stack>
  );
};
